import type { ScenarioContext, SizingMetric } from "@/serverSizing/types";
import type { AiDosLane } from "./adminShared";
import type { RankedCandidateOption } from "./adminApi";

type EquipmentSelection = {
  ids: string[];
  label: string;
  signature: string;
};

type LaneOptions = Partial<Record<AiDosLane, RankedCandidateOption>>;
type DataRecord = Record<string, unknown>;

export type EquipmentComparisonRow = {
  key: string;
  equipment: string;
  engineer: EquipmentSelection;
  ai: Partial<Record<AiDosLane, EquipmentSelection>>;
};

export type MetricComparisonRow = {
  key: string;
  metric: string;
  unit: string;
  engineer: number | null;
  ai: Partial<Record<AiDosLane, number | null>>;
};

const asRecord = (value: unknown): DataRecord =>
  value && typeof value === "object" ? (value as DataRecord) : {};

const asArray = (value: unknown): DataRecord[] =>
  Array.isArray(value) ? value.map(asRecord) : [];

const orderedRows = (equipment: DataRecord, relation: string, orderKey: string) => {
  const rows = asArray(equipment[relation]);
  return Array.isArray(equipment[orderKey]) ? (equipment[orderKey] as unknown[]).map(id => rows.find(row => row.id === id)).filter((row): row is DataRecord => !!row) : rows;
};

const equipmentLabel = (record: DataRecord) => {
  const model =
    record.series_model || record.model || record.configuration || record.name;
  const values = [record.manufacturer, model, record.part_number]
    .filter(Boolean)
    .map(String);
  return [...new Set(values)].join(" · ") || String(record.id || "Unknown");
};

const selection = (
  records: Array<{ record: DataRecord; detail?: string }>
): EquipmentSelection => {
  const validRecords = records.filter(({ record }) => record.id);
  return {
    signature: JSON.stringify(validRecords.map(({ record, detail }) => [record.id, detail ?? null])),
    ids: [
      ...new Set(validRecords.map(({ record }) => String(record.id)))
    ].sort(),
    label:
      validRecords
        .map(({ record, detail }) =>
          detail
            ? `${equipmentLabel(record)} (${detail})`
            : equipmentLabel(record)
        )
        .join("; ") || "Not selected"
  };
};

const directSelection = (
  equipment: DataRecord,
  relationKey: string,
  idKey: string
) => {
  const record = asRecord(equipment[relationKey]);
  const id = record.id || equipment[idKey];
  return selection(id ? [{ record: { ...record, id: String(id) } }] : []);
};

const xrefSelection = (
  equipment: DataRecord,
  relationKey: string,
  recordKey: string,
  idKey: string,
  detail?: (xref: DataRecord) => string | undefined,
  allowedIds?: Set<string>
) =>
  selection(
    orderedRows(equipment, relationKey, `${relationKey.replace('r_xref_', '')}_order`)
      .map(xref => {
        const record = asRecord(xref[recordKey]);
        const id = record.id || xref[idKey];
        return {
          record: { ...record, id: id ? String(id) : undefined },
          detail: detail?.(xref)
        };
      })
      .filter(
        ({ record }) =>
          record.id && (!allowedIds || allowedIds.has(String(record.id)))
      )
  );

const housingSelection = (equipment: DataRecord, allowedIds?: Set<string>) =>
  selection(
    orderedRows(equipment, 'r_xref_pumps', 'pumps_order').flatMap(pump =>
      orderedRows(pump, 'r_xref_housings', 'housings_order')
        .map(xref => {
          const record = asRecord(xref.r_housing);
          const id = record.id || xref.housing_id;
          const stages = xref.stage_count;
          return {
            record: { ...record, id: id ? String(id) : undefined },
            detail: stages ? `${stages} stages` : undefined
          };
        })
        .filter(({ record }) =>
          Boolean(
            record.id && (!allowedIds || allowedIds.has(String(record.id)))
          )
        )
    )
  );

const materialIds = (option?: RankedCandidateOption) =>
  asRecord(asRecord(asRecord(option?.candidatePackage).metadata).materialIds);

const allowedMaterialIds = (
  option: RankedCandidateOption | undefined,
  ...roles: string[]
) => {
  const ids = roles
    .map(role => materialIds(option)[role])
    .filter((id): id is string => typeof id === "string" && Boolean(id))
    .flatMap(id => id.split(",").map(value => value.trim()).filter(Boolean));
  return ids.length ? new Set(ids) : undefined;
};

const selectionForRole = (
  key: string,
  equipment: DataRecord,
  option?: RankedCandidateOption
) => {
  if (key === "pump") {
    return xrefSelection(
      equipment,
      "r_xref_pumps",
      "r_pump",
      "pump_id",
       xref => `${xref.total_stages ?? "Unknown"} stages; ${xref.use_high_strength_shaft ? "high-strength" : "standard"} shaft`,
      allowedMaterialIds(option, "pump")
    );
  }
  if (key === "pump_housing") {
    return housingSelection(
      equipment,
      allowedMaterialIds(option, "pump_housing")
    );
  }
  if (key === "intake") {
    return xrefSelection(
      equipment,
      "r_xref_intakes",
      "r_intake",
      "intake_id",
      undefined,
      allowedMaterialIds(option, "intake")
    );
  }
  if (key === "seal") {
    return xrefSelection(
      equipment,
      "r_xref_seals",
      "r_seal",
      "seal_id",
      undefined,
      allowedMaterialIds(option, "seal")
    );
  }
  if (key === "cable") {
    return xrefSelection(
      equipment,
      "r_xref_cables",
      "r_cable",
      "cable_id",
      xref => (xref.length_feet ? `${xref.length_feet} ft` : undefined),
      allowedMaterialIds(option, "downhole_cable", "surface_cable")
    );
  }

  const direct = (
    {
      motor: ["r_motor", "motor_id"],
      sensor: ["r_sensor", "sensor_id"],
      mle: ["r_mle", "mle_id"],
      vsd: ["r_vsd", "vsd_id"],
      switchboard: ["r_switchboard", "switchboard_id"],
      step_up_transformer: ["r_step_up_transformer", "up_trans_id"],
      step_down_transformer: ["r_step_down_transformer", "down_trans_id"]
    } satisfies Record<string, [string, string]>
  )[key];
  return direct
    ? directSelection(equipment, direct[0], direct[1])
    : selection([]);
};

const EQUIPMENT_ROWS = [
  ["pump", "Pump"],
  ["pump_housing", "Pump housing"],
  ["motor", "Motor"],
  ["intake", "Intake"],
  ["seal", "Seal"],
  ["sensor", "Sensor"],
  ["mle", "Motor lead extension"],
  ["cable", "Cable string"],
  ["vsd", "VSD"],
  ["switchboard", "Switchboard"],
  ["step_up_transformer", "Step-up transformer"],
  ["step_down_transformer", "Step-down transformer"]
] as const;

export const buildEquipmentComparisonRows = (
  context: ScenarioContext,
  options: LaneOptions
): EquipmentComparisonRow[] => {
  const engineerEquipment = asRecord(context.equipment);
  return EQUIPMENT_ROWS.map(([key, equipment]) => ({
    key,
    equipment,
    engineer: selectionForRole(key, engineerEquipment),
    ai: Object.fromEntries(
      Object.entries(options).map(([lane, option]) => [
        lane,
        selectionForRole(
          key,
          comparisonEquipment(option),
          option
        )
      ])
    )
  }));
};

export const selectionsMatch = (
  engineer: EquipmentSelection,
  candidate?: EquipmentSelection
) => {
  if (!candidate) return false;
  if (!engineer.ids.length || !candidate.ids.length) return false;
  return (
    engineer.signature === candidate.signature
  );
};

function comparisonEquipment(option: RankedCandidateOption): DataRecord {
  const pkg = asRecord(option.candidatePackage);
  const equipment = { ...asRecord(pkg.equipment) };
  for (const name of ['pumps', 'intakes', 'seals', 'cables']) {
    if (pkg[name]) equipment[`r_xref_${name}`] = Object.values(asRecord(pkg[name]));
  }
  if (pkg.housings) equipment.r_xref_pumps = asArray(equipment.r_xref_pumps).map(pump => ({
    ...pump, r_xref_housings: (Array.isArray(pump.housings_order) ? pump.housings_order : []).map(id => asRecord(pkg.housings)[String(id)]).filter(Boolean)
  }));
  return equipment;
}

const METRIC_ROWS = [
  ["operatingLiquidRate", "Operating liquid rate"],
  ["tdh", "Total dynamic head"],
  ["requiredStages", "Required stages"],
  ["bepProximity", "BEP proximity"],
  ["pumpEfficiency", "Pump efficiency"],
  ["motorRatedHp", "Motor rated HP"],
  ["brakeHp", "Required brake HP"],
  ["motorMarginPercent", "Motor margin"],
  ["motorLoad", "Motor load"],
  ["voltageDropRatio", "Voltage drop"],
  ["freeGasFraction", "Free gas fraction"],
  ["gasEnteringPump", "Gas entering pump"],
  ["motorInternalTemperature", "Motor internal temperature"],
  ["thrustLoadRatio", "Thrust load"],
  ["casingClearanceMargin", "Casing clearance"]
] as const;

const metric = (metrics: Record<string, unknown>, key: string) =>
  asRecord(metrics[key]) as SizingMetric;

export const buildMetricComparisonRows = (
  baselineMetrics: Record<string, unknown>,
  options: LaneOptions
): MetricComparisonRow[] =>
  METRIC_ROWS.map(([key, label]) => {
    const baseline = metric(baselineMetrics, key);
    const optionMetrics = Object.fromEntries(
      Object.entries(options).map(([lane, option]) => [
        lane,
        metric(option.metricSummary, key)
      ])
    );
    return {
      key,
      metric: label,
      unit:
        baseline.unit ||
        Object.values(optionMetrics).find(value => value.unit)?.unit ||
        "",
      engineer: typeof baseline.value === "number" ? baseline.value : null,
      ai: Object.fromEntries(
        Object.entries(optionMetrics).map(([lane, value]) => [
          lane,
           typeof value.value === "number" && Number.isFinite(value.value) && (!baseline.unit || value.unit === baseline.unit) ? value.value : null
        ])
      )
    };
  }).filter(
    row => row.engineer !== null || Object.values(row.ai).some(v => v !== null)
  );
